import { OggOpusIndex } from "./OggOpusIndex"
import { OpusRegionDecoder } from "./OpusRegionDecoder"

const OUTPUT_CHANNELS = 2
const MIXER_SAMPLE_RATE = 48000
const CHUNK_FRAMES = 4096
const YIELD_INTERVAL_CHUNKS = 25

/**
 * Decodes an entire Opus region into a 48 kHz stereo interleaved PCM buffer.
 *
 * Uses the existing `OpusRegionDecoder` to handle preroll, trimming, and
 * stereo normalisation.  The returned buffer is suitable for wrapping in a
 * `PcmBufferRegionDecoder` or for waveform calculation.
 *
 * @param yieldToEventLoop  When true, yields to the event loop periodically
 *   via `setTimeout(0)` to avoid blocking the UI.  Enable this when decoding
 *   on the main thread (e.g. from `RegionLoader`).
 */
export async function decodeOpusToPcmBuffer(
  index: OggOpusIndex,
  yieldToEventLoop = false,
): Promise<Float32Array> {
  const decoder = new OpusRegionDecoder(index)
  await decoder.ready
  await decoder.seek(0)

  const estimatedFrames = Math.ceil(index.durationSeconds * MIXER_SAMPLE_RATE)
  const chunks: Float32Array[] = []
  let totalSamples = 0
  let chunkCount = 0

  while (!decoder.isExhausted) {
    const chunk = new Float32Array(CHUNK_FRAMES * OUTPUT_CHANNELS)
    decoder.readFramesInto(chunk, CHUNK_FRAMES, 1)
    chunks.push(chunk)
    totalSamples += chunk.length
    chunkCount++

    if (yieldToEventLoop && chunkCount % YIELD_INTERVAL_CHUNKS === 0) {
      await new Promise<void>((resolve) => setTimeout(resolve, 0))
    }
  }

  await decoder.destroy()

  // Trim trailing silence from the last chunk — the decoder may have produced
  // more frames than the actual duration due to Opus packet boundaries.
  const expectedSamples = estimatedFrames * OUTPUT_CHANNELS
  const outputLength = Math.min(totalSamples, expectedSamples)

  const buffer = new Float32Array(outputLength)
  let offset = 0
  for (const chunk of chunks) {
    const remaining = outputLength - offset
    if (remaining <= 0) break
    const toCopy = Math.min(chunk.length, remaining)
    buffer.set(chunk.subarray(0, toCopy), offset)
    offset += toCopy
  }

  return buffer
}
